import React from 'react';
import {Checkbox} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  BriefFieldManagerAttributes,
  ErrorMessages,
} from '@/views/campaigns/brief/brief-field-manager/BriefFieldManager';
import BriefFieldInput from '@/views/campaigns/brief/components/brief-field-input';
import BriefSubSection from '@/views/campaigns/brief/components/campaign-brief/components/brief-sub-section';
import ValidatedField from '@/views/campaigns/brief/components/campaign-brief/components/validated-field';
import {
  SaveCampaign,
  SectionProps,
} from '@/views/campaigns/brief/components/campaign-brief/section/SectionProps';
import CAMPAIGN_BRIEF_TEST_IDS from '@/views/campaigns/brief/components/campaign-brief/testIds';
import {DEFAULT_SAVE_CAMPAIGN_DELAY} from '@/views/campaigns/brief/constants';
import ButtonGroup from '@/components/button-group';
import OptionButton from '@/components/option-button';
import styles from './SendingProducts.module.scss';

const TRANSLATION_PREFIX =
  'components.campaign-brief.sections.sending-products';

const DELIVERABLES_TYPE_OPTIONS = [
  {
    value: null,
    labelLocaleKeyTitle: `${TRANSLATION_PREFIX}.not-relevant`,
    labelLocaleKeySubtitle: `${TRANSLATION_PREFIX}.not-relevant-info`,
  },
  {
    value: 'gift_codes',
    labelLocaleKeyTitle: `${TRANSLATION_PREFIX}.discount-code`,
    labelLocaleKeySubtitle: `${TRANSLATION_PREFIX}.discount-code-info`,
  },
  {
    value: 'shipments',
    labelLocaleKeyTitle: `${TRANSLATION_PREFIX}.ship-product`,
    labelLocaleKeySubtitle: `${TRANSLATION_PREFIX}.ship-product-info`,
  },
];

function ProductShipping({
  editable,
  onCashValueChange,
  onShippingMethodChange,
  deliverablesType,
  estimatedFulfillmentValue,
  saveCampaign,
  errors,
  campaignId,
  briefFieldManager,
}: {
  editable?: boolean;
  onCashValueChange?: (value: any) => void;
  onShippingMethodChange?: (value: any) => void;
  deliverablesType?: 'shipments' | 'gift_codes' | null;
  estimatedFulfillmentValue?: number | null;
  saveCampaign?: SaveCampaign;
  errors?: ErrorMessages;
  campaignId: string;
  briefFieldManager: BriefFieldManagerAttributes;
}) {
  const getCashValueContent = () => {
    return (
      <div className={styles.cashValueContainer}>
        <ValidatedField errors={errors?.estimatedFulfillmentValue}>
          <BriefFieldInput
            testID={CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__CASH_VALUE_INPUT}
            label={translate(`${TRANSLATION_PREFIX}.cash-value`)}
            placeholder="0.00"
            disabled={!deliverablesType || !editable}
            className={styles.cashValueInput}
            value={estimatedFulfillmentValue || ''}
            onInputChange={(value: string) =>
              onCashValueChange?.(value ? Number(value) : value)
            }
            endIcon={
              <label
                className={`${styles.currency} ${
                  !deliverablesType ? styles.disabled : ''
                }`}
              >
                {translate(`${TRANSLATION_PREFIX}.currency`)}
              </label>
            }
            inputProps={{
              type: 'number',
              min: 0,
              value: estimatedFulfillmentValue || '',
              onBlur: () =>
                saveCampaign?.({
                  campaignId,
                  fieldName:
                    briefFieldManager.fields.estimatedFulfillmentValue.key,
                  fieldValue:
                    briefFieldManager.fields.estimatedFulfillmentValue.value,
                }),
            }}
            hasErrors={!!errors?.estimatedFulfillmentValue}
          />
        </ValidatedField>
      </div>
    );
  };

  const getProductShippingContent = () => {
    const selectedOption = DELIVERABLES_TYPE_OPTIONS.find(
      (option) => option.value === deliverablesType
    );

    return (
      <ButtonGroup
        readOnly={!editable}
        testID={CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__SHIPPING_METHOD_INPUT}
        containerClassName={styles.customButtonGroupStyle}
        buttons={DELIVERABLES_TYPE_OPTIONS.map((option) => ({
          ...option,
          title: translate(option.labelLocaleKeyTitle),
          subtitle: translate(option.labelLocaleKeySubtitle),
        }))}
        selectedButtonValue={selectedOption?.value}
        onChange={(button: any) => onShippingMethodChange?.(button.value)}
        renderButton={(button: any, isActive, onClick) => (
          <OptionButton
            button={button}
            className={styles.sendProductButtonContainer}
            onClick={onClick}
            isActive={isActive}
            disabled={!editable}
          >
            <div className={styles.sendProductButton}>
              <div className={styles.checkbox}>
                <Checkbox radio checked={isActive} onChange={() => {}} />
              </div>
              <div className={styles.info}>
                <label
                  data-testid={
                    isActive
                      ? CAMPAIGN_BRIEF_TEST_IDS.CREATOR_PAYMENTS__SHIPPING_METHOD
                      : undefined
                  }
                  className={styles.title}
                >
                  {button.title}
                </label>
                <label className={styles.subtitle}>{button.subtitle}</label>
              </div>
            </div>
          </OptionButton>
        )}
      />
    );
  };

  return (
    <BriefSubSection
      disabled={!editable}
      title={translate(`${TRANSLATION_PREFIX}.product-details`)}
      titleClassName={styles.title}
    >
      <div className={styles.productShippingContainer}>
        <label className={styles.info}>
          {translate(`${TRANSLATION_PREFIX}.product-details-info`)}
        </label>
        <div className={styles.productShipping}>
          <div className={styles.shippingMethod}>
            {getProductShippingContent()}
          </div>
        </div>
        <div className={styles.productCashValue}>
          <div className={styles.cashValue}>{getCashValueContent()}</div>
        </div>
      </div>
    </BriefSubSection>
  );
}

function SendingProducts(props: SectionProps) {
  const {
    updateBriefForm,
    editable,
    saveCampaign,
    briefFieldManager,
    errors,
    campaign,
  } = props;

  return (
    <div className={styles.container}>
      <ProductShipping
        editable={editable}
        onShippingMethodChange={(value) => {
          if (!value) {
            updateBriefForm?.(
              'estimatedFulfillmentValue',
              '',
              'estimatedFulfillmentValue'
            );
          }
          updateBriefForm?.(
            'deliverablesType',
            value,
            'deliverablesType',
            DEFAULT_SAVE_CAMPAIGN_DELAY
          );
        }}
        onCashValueChange={(value) =>
          updateBriefForm?.(
            'estimatedFulfillmentValue',
            value,
            'estimatedFulfillmentValue'
          )
        }
        deliverablesType={
          briefFieldManager.fields.deliverablesType.value || null
        }
        estimatedFulfillmentValue={
          briefFieldManager.fields.estimatedFulfillmentValue.value || ''
        }
        saveCampaign={saveCampaign}
        errors={errors}
        campaignId={campaign.id}
        briefFieldManager={briefFieldManager}
      />
    </div>
  );
}

export default SendingProducts;
